import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Table, App, Spin, Button, Space, Modal, Tag, Input, Select, Row, Col, Statistic, Card } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { apiClient } from '../../utils/api';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, LockOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { StandardCard, PageTitle, ColumnManagerDropdown } from '../common';
import { useColumnManager } from '../../hooks';
import { theme } from '../../theme';

interface LoteData {
  key: string;
  cliente: string;
  cliente_id: number;
  nro_planilla: number;
  nro_lote: number;
  cantidad_fardos: number;
  calidad_desde: string;
  calidad_hasta: string;
  micronaire_desde: number;
  micronaire_hasta: number;
  longitud_desde: number;
  longitud_hasta: number;
  resistencia_desde: number;
  resistencia_hasta: number;
  // Campos HVI
  SCI: number;
  MST: number;
  UHML: number;
  ML: number;
  UI: number;
  STR: number;
  ELG: number;
  MIC: number;
  AMT: number;
  RD: number;
  B: number;
  CG: string;
  T_CNT: number;
  T_AREA: number;
  MR: number;
  SFI: number;
  TRID: number;
  estado: number;
}

interface Cliente {
  id: number;
  denominacion: string;
  nc: string;
}

const EmisionLotes: React.FC = () => {
  const { message } = App.useApp();
  const [data, setData] = useState<LoteData[]>([]);
  const [filteredData, setFilteredData] = useState<LoteData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [estadoFilter, setEstadoFilter] = useState<string>('all');
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  
  // Estados para filtros
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<number | null>(null);
  const [valorCLO, setValorCLO] = useState<number | null>(null);
  
  // Estados para búsqueda de cliente
  const [searchCliente, setSearchCliente] = useState('');
  const [showSearchCliente, setShowSearchCliente] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);

  const [searchText, setSearchText] = useState<Record<string, string>>({});
  const searchInputRefs = useRef<Record<string, any>>({});

  // Lista de todas las columnas disponibles (keys)
  const allColumnKeys = [
    'cliente',
    'nro_lote',
    'cantidad_fardos',
    'cantidad_pendiente',
    'calidad',
    'micronaire',
    'longitud',
    'resistencia',
    'SCI',
    'MST',
    'UHML',
    'ML',
    'UI',
    'STR',
    'ELG',
    'MIC',
    'AMT',
    'RD',
    'B',
    'CG',
    'T_CNT',
    'T_AREA',
    'MR',
    'SFI',
    'TRID',
    'estado',
    'acciones'
  ];

  // Nombres amigables para las columnas
  const columnNames: Record<string, string> = {
    'cliente': 'Cliente',
    'nro_lote': 'N° Lote',
    'cantidad_fardos': 'Cantidad Fardos',
    'cantidad_pendiente': 'Cantidad Pendiente',
    'calidad': 'Calidad',
    'micronaire': 'Micronaire',
    'longitud': 'Longitud',
    'resistencia': 'Resistencia',
    'SCI': 'SCI',
    'MST': 'MST',
    'UHML': 'UHML',
    'ML': 'ML',
    'UI': 'UI',
    'STR': 'STR',
    'ELG': 'ELG',
    'MIC': 'MIC',
    'AMT': 'AMT',
    'RD': 'RD',
    'B': 'B',
    'CG': 'CG',
    'T_CNT': 'T_CNT',
    'T_AREA': 'T_AREA',
    'MR': 'MR',
    'SFI': 'SFI',
    'TRID': 'TRID',
    'estado': 'Estado',
    'acciones': 'Acciones'
  };

  // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
  const columnManager = useColumnManager({
    storageKey: 'emision_lotes',
    allKeys: allColumnKeys,
    requiredKeys: ['cliente', 'nro_lote', 'estado', 'acciones'],
  });

  // Función para filtrar columnas basándose en la visibilidad
  const isColumnVisible = (key: string): boolean => {
    return columnManager.orderedVisibleKeys.includes(key);
  };

  // Función para manejar búsqueda en columnas
  const handleSearch = (value: string, dataIndex: string) => {
    const newSearchText = { ...searchText, [dataIndex]: value };
    setSearchText(newSearchText);
  };

  // Función para limpiar filtros
  const clearFilters = (dataIndex: string) => {
    const newSearchText = { ...searchText };
    delete newSearchText[dataIndex];
    setSearchText(newSearchText);
    handleSearch('', dataIndex);
  };

  const allColumns: ColumnsType<LoteData> = [
    {
      title: 'Cliente',
      dataIndex: 'cliente',
      key: 'cliente',
      width: 180,
      fixed: 'left',
      sorter: (a, b) => a.cliente.localeCompare(b.cliente),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        const currentSearch = searchText['cliente'] || '';
        return (
          <div style={{ padding: 8 }}>
            <Input
              ref={(node) => {
                if (node) searchInputRefs.current['cliente'] = node;
              }}
              placeholder={`Buscar Cliente`}
              value={currentSearch}
              onChange={(e) => {
                const newSearchText = { ...searchText, cliente: e.target.value };
                setSearchText(newSearchText);
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              style={{ marginBottom: 8, display: 'block', width: 188 }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                }}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
              >
                Buscar
              </Button>
              <Button 
                onClick={() => {
                  if (clearFilters) {
                    clearFilters();
                  }
                  const newSearchText = { ...searchText };
                  delete newSearchText['cliente'];
                  setSearchText(newSearchText);
                }} 
                size="small" 
                style={{ width: 90 }}
              >
                Limpiar
              </Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? theme.colors.secondary : theme.colors.textSecondary }} />
      ),
      onFilter: (value, record) =>
        record.cliente.toString().toLowerCase().includes((value as string).toLowerCase()),
    },
    {
      title: 'N° Lote',
      dataIndex: 'nro_lote',
      key: 'nro_lote',
      width: 110,
      align: 'right',
      fixed: 'left',
      sorter: (a, b) => a.nro_lote - b.nro_lote,
    },
    {
      title: 'Cantidad Fardos',
      dataIndex: 'cantidad_fardos',
      key: 'cantidad_fardos',
      width: 130,
      align: 'right',
      sorter: (a, b) => a.cantidad_fardos - b.cantidad_fardos,
    },
    {
      title: 'Cantidad Pendiente',
      key: 'cantidad_pendiente',
      width: 150,
      align: 'right',
      render: (_: any, record: LoteData) => {
        const clo = valorCLO ?? 0;
        const pendiente = clo - (record.cantidad_fardos || 0);
        const color = pendiente < 0 ? '#ff4d4f' : pendiente > 0 ? '#52c41a' : '#000000';
        return <span style={{ color, fontWeight: 'bold' }}>{pendiente}</span>;
      },
      sorter: (a, b) => {
        const clo = valorCLO ?? 0;
        const pa = clo - (a.cantidad_fardos || 0);
        const pb = clo - (b.cantidad_fardos || 0);
        return pa - pb;
      }
    },
    {
      title: 'Calidad',
      children: [
        {
          title: 'Desde',
          dataIndex: 'calidad_desde',
          key: 'calidad_desde',
          width: 110,
          align: 'center',
          sorter: (a, b) => a.calidad_desde.localeCompare(b.calidad_desde),
        },
        {
          title: 'Hasta',
          dataIndex: 'calidad_hasta',
          key: 'calidad_hasta',
          width: 110,
          align: 'center',
          sorter: (a, b) => a.calidad_hasta.localeCompare(b.calidad_hasta),
        },
      ],
    },
    {
      title: 'Micronaire',
      children: [
        {
          title: 'Desde',
          dataIndex: 'micronaire_desde',
          key: 'micronaire_desde',
          width: 110,
          align: 'right',
          render: (value: number) => value.toFixed(2),
          sorter: (a, b) => a.micronaire_desde - b.micronaire_desde,
        },
        {
          title: 'Hasta',
          dataIndex: 'micronaire_hasta',
          key: 'micronaire_hasta',
          width: 110,
          align: 'right',
          render: (value: number) => value.toFixed(2),
          sorter: (a, b) => a.micronaire_hasta - b.micronaire_hasta,
        },
      ],
    },
    {
      title: 'Longitud',
      children: [
        {
          title: 'Desde',
          dataIndex: 'longitud_desde',
          key: 'longitud_desde',
          width: 110,
          align: 'right',
          render: (value: number) => value.toFixed(2),
          sorter: (a, b) => a.longitud_desde - b.longitud_desde,
        },
        {
          title: 'Hasta',
          dataIndex: 'longitud_hasta',
          key: 'longitud_hasta',
          width: 110,
          align: 'right',
          render: (value: number) => value.toFixed(2),
          sorter: (a, b) => a.longitud_hasta - b.longitud_hasta,
        },
      ],
    },
    {
      title: 'Resistencia',
      children: [
        {
          title: 'Desde',
          dataIndex: 'resistencia_desde',
          key: 'resistencia_desde',
          width: 110,
          align: 'right',
          render: (value: number) => value.toFixed(2),
          sorter: (a, b) => a.resistencia_desde - b.resistencia_desde,
        },
        {
          title: 'Hasta',
          dataIndex: 'resistencia_hasta',
          key: 'resistencia_hasta',
          width: 110,
          align: 'right',
          render: (value: number) => value.toFixed(2),
          sorter: (a, b) => a.resistencia_hasta - b.resistencia_hasta,
        },
      ],
    },
    // Columnas HVI
    {
      title: 'SCI',
      dataIndex: 'SCI',
      key: 'SCI',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.SCI || 0) - (b.SCI || 0),
    },
    {
      title: 'MST',
      dataIndex: 'MST',
      key: 'MST',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.MST || 0) - (b.MST || 0),
    },
    {
      title: 'UHML',
      dataIndex: 'UHML',
      key: 'UHML',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.UHML || 0) - (b.UHML || 0),
    },
    {
      title: 'ML',
      dataIndex: 'ML',
      key: 'ML',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.ML || 0) - (b.ML || 0),
    },
    {
      title: 'UI',
      dataIndex: 'UI',
      key: 'UI',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.UI || 0) - (b.UI || 0),
    },
    {
      title: 'STR',
      dataIndex: 'STR',
      key: 'STR',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.STR || 0) - (b.STR || 0),
    },
    {
      title: 'ELG',
      dataIndex: 'ELG',
      key: 'ELG',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.ELG || 0) - (b.ELG || 0),
    },
    {
      title: 'MIC',
      dataIndex: 'MIC',
      key: 'MIC',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.MIC || 0) - (b.MIC || 0),
    },
    {
      title: 'AMT',
      dataIndex: 'AMT',
      key: 'AMT',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.AMT || 0) - (b.AMT || 0),
    },
    {
      title: 'RD',
      dataIndex: 'RD',
      key: 'RD',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.RD || 0) - (b.RD || 0),
    },
    {
      title: 'B',
      dataIndex: 'B',
      key: 'B',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.B || 0) - (b.B || 0),
    },
    {
      title: 'CG',
      dataIndex: 'CG',
      key: 'CG',
      width: 90,
      align: 'center',
      render: (value: string) => value || '',
      sorter: (a, b) => {
        const aVal = a.CG || '';
        const bVal = b.CG || '';
        return aVal.localeCompare(bVal);
      },
    },
    {
      title: 'T_CNT',
      dataIndex: 'T_CNT',
      key: 'T_CNT',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.T_CNT || 0) - (b.T_CNT || 0),
    },
    {
      title: 'T_AREA',
      dataIndex: 'T_AREA',
      key: 'T_AREA',
      width: 100,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.T_AREA || 0) - (b.T_AREA || 0),
    },
    {
      title: 'MR',
      dataIndex: 'MR',
      key: 'MR',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.MR || 0) - (b.MR || 0),
    },
    {
      title: 'SFI',
      dataIndex: 'SFI',
      key: 'SFI',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.SFI || 0) - (b.SFI || 0),
    },
    {
      title: 'TRID',
      dataIndex: 'TRID',
      key: 'TRID',
      width: 90,
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00',
      sorter: (a, b) => (a.TRID || 0) - (b.TRID || 0),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 110,
      align: 'center',
      render: (estado: number) => (
        <Tag color={estado === 2 ? 'red' : 'green'}>
          {estado === 2 ? 'Cerrado' : 'Abierto'}
        </Tag>
      ),
      filters: [
        { text: 'Abierto', value: 1 },
        { text: 'Cerrado', value: 2 },
      ],
      onFilter: (value, record) => record.estado === value,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 180,
      fixed: 'right',
      render: (_: any, record: LoteData) => {
        const cierreDisabled = record.estado === 2 || record.nro_lote === 0;
        return (
          <Space size="small">
            <Button
              type="primary"
              icon={<LockOutlined />}
              size="small"
              onClick={() => handleCierre(record.key)}
              disabled={cierreDisabled}
              style={{
                backgroundColor: cierreDisabled ? undefined : theme.colors.success,
                borderColor: cierreDisabled ? undefined : theme.colors.success,
                color: cierreDisabled ? undefined : 'white'
              }}
            >
              Cierre
            </Button>
            <Button
              icon={<FileTextOutlined />}
              size="small"
              onClick={() => handleEmision(record)}
              style={{
                backgroundColor: theme.colors.info,
                borderColor: theme.colors.info,
                color: 'white'
              }}
            >
              Emisión
            </Button>
          </Space>
        );
      },
    },
  ];

  // Título de columnas agrupadas -> clave del selector de columnas
  const groupTitleToKey: Record<string, string> = {
    'Calidad': 'calidad',
    'Micronaire': 'micronaire',
    'Longitud': 'longitud',
    'Resistencia': 'resistencia',
  };

  // Columnas visibles, en el orden elegido por el usuario en el selector de columnas.
  // Las columnas agrupadas (Calidad/Micronaire/Longitud/Resistencia) se mueven como un solo bloque.
  const columns = useMemo(() => {
    const columnsByKey = new Map<string, (typeof allColumns)[number]>();
    allColumns.forEach(col => {
      if ('children' in col && col.children) {
        const title = typeof col.title === 'string' ? col.title : '';
        const mappedKey = groupTitleToKey[title];
        if (mappedKey) columnsByKey.set(mappedKey, col);
      } else if (col.key) {
        columnsByKey.set(col.key as string, col);
      }
    });
    return columnManager.orderedVisibleKeys
      .map(key => columnsByKey.get(key))
      .filter((col): col is (typeof allColumns)[number] => Boolean(col));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnManager.orderedVisibleKeys, allColumns, valorCLO]);

  // Cargar clientes al montar el componente
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await apiClient.get<Cliente[]>(
          `${process.env.REACT_APP_PADRON_URL}/leer_padron/${user?.id_empresa}/`,
          { token }
        );
        setClientes(response.data || []);
      } catch (error) {
        console.error('Error al cargar los clientes:', error);
        message.error('Error al cargar la lista de clientes');
      }
    };

    if (user?.id_empresa) {
      fetchClientes();
    }
  }, [user, getAccessTokenSilently, message]);

  // Cargar cantidad de fardos por lote (CLO) desde RELACION
  useEffect(() => {
    const fetchCLO = async () => {
      try {
        const token = await getAccessTokenSilently();
        const resp = await apiClient.get<any[]>(
          `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${user?.id_empresa}/${user?.id_sucursal || 0}/CLO`,
          { token }
        );
        const primero = Array.isArray(resp.data) ? resp.data[0] : null;
        const v = primero?.valor_numerico ? parseFloat(primero.valor_numerico) : null;
        setValorCLO(isNaN(v as any) ? null : v);
      } catch (e) {
        setValorCLO(null);
      }
    };
    if (user?.id_empresa) fetchCLO();
  }, [user, getAccessTokenSilently]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.search-dropdown')) {
        setShowSearchCliente(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Función para buscar lotes con filtros
  const handleBuscar = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      // Construir parámetros de búsqueda
      const params: Record<string, string> = {};
      if (clienteSeleccionado) {
        params.cliente_id = clienteSeleccionado.toString();
      }
      
      const response = await apiClient.get<LoteData[]>(
        `${process.env.REACT_APP_CLASIFICACION_URL}/emision_lotes/${user?.id_empresa}`,
        { token, params }
      );

      if (Array.isArray(response.data)) {
        const dataConKey = response.data.map((item: any) => ({
          ...item,
          key: `${item.cliente_id || 0}-${item.nro_lote}`,
        }));
        setData(dataConKey);
        setFilteredData(dataConKey);
      } else {
        throw new Error('La respuesta no es un array');
      }
    } catch (error) {
      console.error('Error al cargar el listado de lotes:', error);
      message.error('Error al cargar el listado de lotes');
    } finally {
      setLoading(false);
    }
  };

  // Función para filtrar por estado
  const handleEstadoFilter = (value: string) => {
    setEstadoFilter(value);
    let filtered = data;
    
    // Aplicar filtro de estado
    if (value !== 'all') {
      filtered = filtered.filter(item => item.estado === parseInt(value));
    }
    
    // Aplicar filtro de búsqueda de cliente
    if (searchText['cliente']) {
      const searchLower = searchText['cliente'].toLowerCase();
      filtered = filtered.filter(item =>
        item.cliente.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredData(filtered);
  };

  // Aplicar filtros cuando cambien los datos o el texto de búsqueda
  useEffect(() => {
    let filtered = data;
    
    // Aplicar filtro de estado
    if (estadoFilter !== 'all') {
      filtered = filtered.filter(item => item.estado === parseInt(estadoFilter));
    }
    
    // Aplicar filtro de búsqueda de cliente
    if (searchText['cliente']) {
      const searchLower = searchText['cliente'].toLowerCase();
      filtered = filtered.filter(item =>
        item.cliente.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredData(filtered);
  }, [data, estadoFilter, searchText]);

  const handleCierre = async (loteKey: string) => {
    if (!loteKey) {
      message.warning('Por favor seleccione un lote');
      return;
    }

    const [clienteId, nroLote] = loteKey.split('-');
    
    // Si el clienteId es 0 o undefined, enviamos null al backend
    const idToUse = (clienteId === '0' || clienteId === 'undefined' || !clienteId) 
      ? 'null'  // Enviamos 'null' como string para que el backend lo interprete como null
      : clienteId;

    // Verificar si el lote ya está cerrado
    const selectedLoteData = data.find(item => item.key === loteKey);
    if (selectedLoteData?.estado === 2) {
      message.warning('Este lote ya está cerrado');
      return;
    }

    Modal.confirm({
      title: 'Confirmar Cierre de Lote',
      content: '¿Desea Cerrar el Lote? No va a poder agregar ni cambiar fardos del mismo.',
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getAccessTokenSilently();
          
          await apiClient.post(
            `${process.env.REACT_APP_CLASIFICACION_URL}/cerrar_lote/${user?.id_empresa}/${idToUse}/${nroLote}/`,
            {},
            { token }
          );

          message.success('Lote cerrado exitosamente');
          // Recargar los datos para actualizar el estado
          const updatedResponse = await apiClient.get<LoteData[]>(
            `${process.env.REACT_APP_CLASIFICACION_URL}/emision_lotes/${user?.id_empresa}`,
            { token }
          );
          if (Array.isArray(updatedResponse.data)) {
            const dataConKey = updatedResponse.data.map((item: any) => ({
              ...item,
              key: `${item.cliente_id === null ? '0' : item.cliente_id}-${item.nro_lote}`,
            }));
            setData(dataConKey);
            setFilteredData(dataConKey);
          }
        } catch (error) {
          console.error('Error al cerrar el lote:', error);
          message.error('Error al cerrar el lote');
        }
      }
    });
  };

  const handleEmision = (record: LoteData) => {
    navigate(`/listado_lote/${record.nro_lote}`);
  };

  return (
    <div style={{ padding: '0' }}>
      <PageTitle>Listado de Lotes</PageTitle>
      
      {loading && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(255, 255, 255, 0.8)', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          zIndex: 1000
        }}>
          <Spin size="large" />
        </div>
      )}
      
      <StandardCard 
        title="Filtros de Búsqueda"
        style={{ marginBottom: '16px' }}
        headStyle={{
          padding: '8px 16px',
          fontSize: '14px'
        }}
        bodyStyle={{ padding: '12px 16px' }}
      >
      {/* Sección de Filtros */}
      <Row gutter={[12, 12]} style={{ marginBottom: '12px' }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ marginBottom: '4px', fontWeight: 500, fontSize: '13px' }}>Cliente</div>
          <div style={{ position: 'relative' }} className="search-dropdown">
            <input
              type="text"
              placeholder="Buscar por denominación..."
              style={{
                width: '100%',
                paddingLeft: '28px',
                paddingRight: selectedCliente ? '28px' : undefined,
                paddingTop: '5px',
                paddingBottom: '5px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                marginBottom: '4px',
                backgroundColor: selectedCliente ? '#f5f5f5' : 'white',
                cursor: 'pointer',
                fontSize: '13px',
                height: '32px'
              }}
              value={selectedCliente ? `${selectedCliente.denominacion} - ${selectedCliente.nc}` : searchCliente}
              onChange={e => {
                setSearchCliente(e.target.value);
                setSelectedCliente(null);
                setShowSearchCliente(true);
                setClienteSeleccionado(null);
              }}
              onFocus={() => setShowSearchCliente(true)}
            />
            {/* Lupa */}
            <SearchOutlined style={{
              position: 'absolute',
              left: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              pointerEvents: 'none',
              fontSize: '14px'
            }} />
            {/* Limpiar selección */}
            {selectedCliente && (
              <span
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  zIndex: 1100
                }}
                onClick={() => {
                  setSelectedCliente(null);
                  setSearchCliente('');
                  setShowSearchCliente(true);
                  setClienteSeleccionado(null);
                }}
                title="Limpiar selección"
              >
                ×
              </span>
            )}
            {/* Dropdown */}
            {showSearchCliente && clientes.length > 0 && !selectedCliente && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                marginBottom: '4px'
              }}>
                {clientes.filter(cliente => {
                  const search = searchCliente.trim().toLowerCase();
                  return (
                    cliente.denominacion.toLowerCase().includes(search) ||
                    cliente.nc.toLowerCase().includes(search)
                  );
                }).map(cliente => (
                  <div
                    key={cliente.id}
                    style={{
                      padding: '6px 10px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      fontSize: '13px'
                    }}
                    onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => {
                      setSelectedCliente(cliente);
                      setSearchCliente('');
                      setShowSearchCliente(false);
                      setClienteSeleccionado(cliente.id);
                    }}
                  >
                    {cliente.nc} - {cliente.denominacion}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ marginBottom: '4px', fontWeight: 500, fontSize: '13px' }}>Estado</div>
          <Select
            defaultValue="all"
            style={{ width: '100%' }}
            onChange={handleEstadoFilter}
            size="middle"
            options={[
              { value: 'all', label: 'Todos' },
              { value: '1', label: 'Abiertos' },
              { value: '2', label: 'Cerrados' },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ marginBottom: '4px', fontWeight: 500, fontSize: '13px', color: 'transparent' }}>&nbsp;</div>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleBuscar}
            style={{ width: '100%', height: '32px', fontSize: '13px' }}
            size="middle"
          >
            Buscar
          </Button>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card 
            size="small" 
            style={{ 
              height: '100%', 
              backgroundColor: theme.colors.info, 
              color: 'white',
              borderColor: theme.colors.info,
              width: '100%'
            }}
            bodyStyle={{ padding: '10px 12px' }}
          >
            <Statistic
              title={<div style={{ color: 'white', fontWeight: 600, textAlign: 'center', fontSize: '12px' }}>FARDOS P/LOTE</div>}
              value={valorCLO ?? '-'}
              precision={valorCLO !== null ? 0 : undefined}
              valueStyle={{ color: 'white', fontSize: '20px' }}
              style={{ textAlign: 'right' }}
            />
          </Card>
        </Col>
      </Row>
      </StandardCard>

      <StandardCard 
        title="Lotes"
        style={{ marginBottom: '16px' }}
        headStyle={{
          padding: '8px 16px',
          fontSize: '14px'
        }}
        bodyStyle={{ padding: '12px' }}
        extra={
          <Space>
            <Button 
              type="primary" 
              onClick={() => navigate('/clasificacion/loteo/')}
              size="small"
              style={{ 
                backgroundColor: theme.colors.secondary,
                borderColor: theme.colors.secondary,
                minWidth: '100px',
                fontSize: '13px',
                fontWeight: 600
              }}
            >
              Loteo
            </Button>
            <ColumnManagerDropdown
              manager={columnManager}
              labels={columnNames}
              requiredKeys={['cliente', 'nro_lote', 'estado', 'acciones']}
              buttonSize="small"
            />
          </Space>
        }
      >
      <Table
        columns={columns}
        dataSource={filteredData}
        scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 500px))' }}
        pagination={{
          position: ['bottomRight'],
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
          pageSize: 50,
          pageSizeOptions: ['10', '25', '50', '100'],
        }}
        bordered
        size="middle"
        onRow={(record) => ({
          style: {
            backgroundColor: record.estado === 2 ? '#fff1f0' : 'white',
          },
        })}
      />
      </StandardCard>
    </div>
  );
};

export default EmisionLotes;
